import type { IInvestorRepository } from "@/application/ports/repositories";
import type { BulkResult } from "@/domain/types/bulk";
import type {
  CompanyInvestor,
  CreateCompanyInvestorRequest,
  CreateIndividualInvestorRequest,
  HubInvitationRequest,
  IndividualInvestor,
  Investor,
  InvestorAggregates,
  InvestorAggregatesParams,
  InvestorListFilters,
  InvestorListParams,
  InvestorSort,
  InvestorExportParams,
  InvestorDocumentType,
  InvestorLegalRepCreateRequest,
  InvestorLegalRepresentative,
  InvestorLegalRepUpdateRequest,
  InvestorListResponse,
  InvestorMetrics,
  InvestorOperationResponse,
  InvestorStatus,
  InvestorType,
  UpdateCompanyInvestorRequest,
  UpdateIndividualInvestorRequest,
} from "@/domain/investor";
import type { OrganizationInvitation } from "@/domain/invitation";
import type {
  DocumentWithReviewResponse,
  DocumentDownloadResponse,
  InvestorPortfolioResponse,
  ReviewUpdateRequest,
  DocumentUpdateRequest,
} from "@/domain/documentBlocks";
import type { ExportDownloadOptions, ExportPreview } from "@/domain/export";
import type { Language } from "@/domain/common";
import type { DocumentStatus, DocumentType } from "@/domain/documentEnums";
import { ApiClientError } from "@/application/errors/ApiClientError";
import { downloadBlob } from "../fileDownload";
import type { CompanyResponse, IndividualResponse } from "../generated/types.gen";
import * as sdk from "../generated/sdk.gen";
import { unwrap, unwrapBlobWithFilename } from "../unwrap";

function toLanguage(value: string): Language {
  return value === "en" || value === "ca" ? value : "es";
}

function mapIndividual(investor: IndividualResponse): IndividualInvestor {
  return { ...investor, communication_language: toLanguage(investor.communication_language) };
}

function mapCompany(investor: CompanyResponse): CompanyInvestor {
  return { ...investor, communication_language: toLanguage(investor.communication_language) };
}

function toFilterQuery(filters: InvestorListFilters) {
  const many = <T>(values: T[] | undefined) => (values && values.length > 0 ? values : undefined);
  return {
    search: filters.search || undefined,
    status: many(filters.status),
    investor_type: many(filters.investor_type),
    tag: many(filters.tag),
    uuid: filters.uuid ? [filters.uuid] : undefined,
    fiscal_year: filters.fiscal_year,
  };
}

const toSortQuery = (sort: InvestorSort[] | undefined) =>
  sort && sort.length > 0
    ? sort.map((entry) => `${entry.key}:${entry.direction}`).join(",")
    : undefined;

const toMessageBody = (request: HubInvitationRequest | undefined) =>
  request?.message?.trim() ? { message: request.message } : undefined;

export class InvestorRepository implements IInvestorRepository {
  async list(params: InvestorListParams = {}, signal?: AbortSignal): Promise<InvestorListResponse> {
    const { sort, limit, offset, ...filters } = params;
    const { items, ...rest } = await unwrap(
      sdk.listInvestors({
        query: { ...toFilterQuery(filters), sort: toSortQuery(sort), limit, offset },
        signal,
      }),
    );
    return { investors: items, ...rest };
  }

  async getAggregates(
    params: InvestorAggregatesParams,
    signal?: AbortSignal,
  ): Promise<InvestorAggregates> {
    const { aggregates, ...filters } = params;
    const response = await unwrap(
      sdk.getInvestorAggregates({
        query: { ...toFilterQuery(filters), aggregates: aggregates.join(",") },
        signal,
      }),
    );
    return response.aggregates;
  }

  async getInvestor(uuid: string, signal?: AbortSignal): Promise<Investor> {
    const investor = await unwrap(sdk.getInvestor({ path: { investor_uuid: uuid }, signal }));
    return investor.type === "company" ? mapCompany(investor) : mapIndividual(investor);
  }

  async getMetrics(params?: {
    status?: InvestorStatus;
    investor_type?: InvestorType;
    search?: string;
  }): Promise<InvestorMetrics> {
    const query = params && {
      search: params.search,
      status: params.status ? [params.status] : undefined,
      investor_type: params.investor_type ? [params.investor_type] : undefined,
    };
    return unwrap(sdk.getInvestorMetrics({ query }));
  }

  async createIndividual(data: CreateIndividualInvestorRequest): Promise<IndividualInvestor> {
    return mapIndividual(await unwrap(sdk.createIndividualInvestor({ body: data })));
  }

  async getIndividual(uuid: string): Promise<IndividualInvestor> {
    return mapIndividual(
      await unwrap(sdk.getIndividualInvestor({ path: { investor_uuid: uuid } })),
    );
  }

  async updateIndividual(
    uuid: string,
    data: UpdateIndividualInvestorRequest,
  ): Promise<IndividualInvestor> {
    return mapIndividual(
      await unwrap(sdk.updateIndividualInvestor({ path: { investor_uuid: uuid }, body: data })),
    );
  }

  async createCompany(data: CreateCompanyInvestorRequest): Promise<CompanyInvestor> {
    return mapCompany(await unwrap(sdk.createCompanyInvestor({ body: data })));
  }

  async getCompany(uuid: string): Promise<CompanyInvestor> {
    return mapCompany(await unwrap(sdk.getCompanyInvestor({ path: { investor_uuid: uuid } })));
  }

  async updateCompany(uuid: string, data: UpdateCompanyInvestorRequest): Promise<CompanyInvestor> {
    return mapCompany(
      await unwrap(sdk.updateCompanyInvestor({ path: { investor_uuid: uuid }, body: data })),
    );
  }

  // --- Legal Representatives ---

  async listLegalRepresentatives(investorUuid: string): Promise<InvestorLegalRepresentative[]> {
    const { items } = await unwrap(
      sdk.listInvestorLegalRepresentatives({ path: { investor_uuid: investorUuid } }),
    );
    return items;
  }

  async createLegalRepresentative(
    investorUuid: string,
    data: InvestorLegalRepCreateRequest,
  ): Promise<InvestorLegalRepresentative> {
    return unwrap(
      sdk.createInvestorLegalRepresentative({
        path: { investor_uuid: investorUuid },
        body: data,
      }),
    );
  }

  async updateLegalRepresentative(
    investorUuid: string,
    representativeUuid: string,
    data: InvestorLegalRepUpdateRequest,
  ): Promise<void> {
    await unwrap(
      sdk.updateInvestorLegalRepresentative({
        path: { investor_uuid: investorUuid, representative_uuid: representativeUuid },
        body: data,
      }),
    );
  }

  async setPrimaryLegalRepresentative(
    investorUuid: string,
    representativeUuid: string,
  ): Promise<void> {
    await unwrap(
      sdk.setPrimaryInvestorLegalRepresentative({
        path: { investor_uuid: investorUuid, representative_uuid: representativeUuid },
      }),
    );
  }

  async deleteLegalRepresentative(investorUuid: string, representativeUuid: string): Promise<void> {
    await unwrap(
      sdk.deleteInvestorLegalRepresentative({
        path: { investor_uuid: investorUuid, representative_uuid: representativeUuid },
      }),
    );
  }

  async delete(uuid: string): Promise<void> {
    await unwrap(sdk.deleteInvestor({ path: { investor_uuid: uuid } }));
  }

  async activate(uuid: string): Promise<InvestorOperationResponse> {
    return unwrap(sdk.activateInvestor({ path: { investor_uuid: uuid } }));
  }

  async deactivate(uuid: string): Promise<InvestorOperationResponse> {
    return unwrap(sdk.deactivateInvestor({ path: { investor_uuid: uuid } }));
  }

  async archive(uuid: string): Promise<InvestorOperationResponse> {
    return unwrap(sdk.archiveInvestor({ path: { investor_uuid: uuid } }));
  }

  async bulkActivate(uuids: readonly string[]): Promise<BulkResult> {
    return unwrap(sdk.bulkActivateInvestors({ body: { investor_uuids: [...uuids] } }));
  }

  async bulkDeactivate(uuids: readonly string[]): Promise<BulkResult> {
    return unwrap(sdk.bulkDeactivateInvestors({ body: { investor_uuids: [...uuids] } }));
  }

  async bulkArchive(uuids: readonly string[]): Promise<BulkResult> {
    return unwrap(sdk.bulkArchiveInvestors({ body: { investor_uuids: [...uuids] } }));
  }

  async bulkDelete(uuids: readonly string[]): Promise<BulkResult> {
    return unwrap(sdk.bulkDeleteInvestors({ body: { investor_uuids: [...uuids] } }));
  }

  async inviteToHub(uuid: string, request?: HubInvitationRequest): Promise<OrganizationInvitation> {
    return unwrap(
      sdk.inviteInvestorToHub({ path: { investor_uuid: uuid }, body: toMessageBody(request) }),
    );
  }

  async resendHubInvitation(invitationUuid: string, request?: HubInvitationRequest): Promise<void> {
    await unwrap(
      sdk.resendInvitation({
        path: { invitation_uuid: invitationUuid },
        body: toMessageBody(request),
      }),
    );
  }

  async revokeHubInvitation(invitationUuid: string): Promise<void> {
    await unwrap(sdk.revokeInvitation({ path: { invitation_uuid: invitationUuid } }));
  }

  async revokeHubAccess(uuid: string): Promise<void> {
    await unwrap(sdk.revokeInvestorHubAccess({ path: { investor_uuid: uuid } }));
  }

  async listDocuments(
    investorUuid: string,
    params?: {
      document_type?: DocumentType;
      status?: DocumentStatus;
      fiscal_quota_uuid?: string;
      search?: string;
      limit?: number;
      offset?: number;
    },
    signal?: AbortSignal,
  ): Promise<InvestorPortfolioResponse> {
    return unwrap(
      sdk.listInvestorDocuments({
        path: { investor_uuid: investorUuid },
        query: params,
        signal,
      }),
    );
  }

  async uploadDocument(
    investorUuid: string,
    file: File,
    docType: InvestorDocumentType,
    title?: string,
  ): Promise<DocumentWithReviewResponse> {
    return unwrap(
      sdk.uploadInvestorDocument({
        path: { investor_uuid: investorUuid },
        body: { file, doc_type: docType, title },
      }),
    );
  }

  async getDocument(
    investorUuid: string,
    documentUuid: string,
  ): Promise<DocumentWithReviewResponse> {
    return unwrap(
      sdk.getInvestorDocument({
        path: { investor_uuid: investorUuid, document_uuid: documentUuid },
      }),
    );
  }

  async getDocumentUrl(
    investorUuid: string,
    documentUuid: string,
  ): Promise<DocumentDownloadResponse> {
    return unwrap(
      sdk.downloadInvestorDocument({
        path: { investor_uuid: investorUuid, document_uuid: documentUuid },
      }),
    );
  }

  async downloadDocument(
    investorUuid: string,
    documentUuid: string,
    fileName?: string,
  ): Promise<void> {
    const documentResponse = await this.getDocumentUrl(investorUuid, documentUuid);

    const actualFileName = documentResponse.filename || fileName || `document-${documentUuid}.pdf`;

    const response = await fetch(documentResponse.signed_url, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      headers: { Accept: "application/pdf" },
    });

    if (!response.ok) {
      throw new ApiClientError({
        message: `Failed to download document: ${response.status} ${response.statusText}`,
        code: "DOWNLOAD_ERROR",
        status: response.status,
      });
    }

    const blob = await response.blob();
    downloadBlob(blob, actualFileName);
  }

  async deleteDocument(investorUuid: string, documentUuid: string): Promise<void> {
    await unwrap(
      sdk.deleteInvestorDocument({
        path: { investor_uuid: investorUuid, document_uuid: documentUuid },
      }),
    );
  }

  async reviewDocument(
    investorUuid: string,
    documentUuid: string,
    review: ReviewUpdateRequest,
  ): Promise<DocumentWithReviewResponse> {
    return unwrap(
      sdk.reviewInvestorDocument({
        path: { investor_uuid: investorUuid, document_uuid: documentUuid },
        body: review,
      }),
    );
  }

  async updateDocument(
    investorUuid: string,
    documentUuid: string,
    data: DocumentUpdateRequest,
  ): Promise<DocumentWithReviewResponse> {
    return unwrap(
      sdk.updateInvestorDocument({
        path: { investor_uuid: investorUuid, document_uuid: documentUuid },
        body: data,
      }),
    );
  }

  async previewExport(params: InvestorExportParams, signal?: AbortSignal): Promise<ExportPreview> {
    const { sort: _sort, ...filters } = params;
    return unwrap(sdk.previewInvestorsExport({ query: toFilterQuery(filters), signal }));
  }

  async downloadExport(
    params: InvestorExportParams,
    options: ExportDownloadOptions,
  ): Promise<void> {
    const { blob, filename } = await unwrapBlobWithFilename(
      sdk.exportInvestors,
      {
        query: {
          ...toFilterQuery(params),
          format: options.format,
          columns: options.columns.length > 0 ? options.columns.join(",") : undefined,
          sort: toSortQuery(params.sort),
        },
      },
      `investors.${options.format}`,
    );
    downloadBlob(blob, filename);
  }
}
